<template>
  <v-select
    v-model="selectedFilters"
    label="Search Filters"
    :items="filters"
    :disabled="disabled"
    item-text="name"
    item-value="key"
    background-color="input"
    style="border-top-right-radius: 0px; border-bottom-right-radius: 0px; border-right: 0px;"
    :menu-props="{ offsetY: true, maxHeight: '600' }"
    multiple
    outlined
    hide-details
    small-chips
    dense>
    <template v-slot:selection="{ attrs, item }">
      <template v-if="item.key === 'vendor_ids' && (params.vendor_ids.length > 0)">
        <v-chip
          v-for="vendor in selectedVendors"
          :key="vendor.id"
          small>
          {{ vendor.name }}
          <v-icon
            small
            right
            @click="removeVendor(vendor.id)">
            $delete
          </v-icon>
        </v-chip>
      </template>
      <template v-else-if="item.key === 'item_sources' && (params.item_sources.length > 0)">
        <v-chip
          v-for="source in selectedSources"
          :key="source.source_id"
          small>
          {{ source.display_name }}
          <v-icon
            small
            right
            @click="removeSource(source)">
            $delete
          </v-icon>
        </v-chip>
      </template>
      <template v-else-if="['private_label', 'shipper_flag'].includes(item.key)">
        <v-chip :key="item.key" color="primary" small>
          {{ item.name }}
          <v-icon
            small
            right
            @click="removeFilter(item.key, true)">
            $delete
          </v-icon>
        </v-chip>
      </template>
      <v-chip
        v-else
        v-bind="attrs"
        small>
        {{ getFilterText(item) }}
        <v-icon
          small
          right
          @click="removeFilter(item.key, true)">
          $delete
        </v-icon>
      </v-chip>
    </template>
    <template v-slot:item="{ item, attrs }">
      <v-list-item 
        :style="item.type === 'boolean' ? 'display: none;' : ''"
        :disabled="item.type === 'boolean'">
        <v-list-item-content>
          <VendorSelect
            v-if="item.key === 'vendor_ids'"
            :selectedVendors.sync="selectedVendors">
          </VendorSelect>
          <v-autocomplete
            v-else-if="item.key === 'item_sources'"
            v-model="selectedSources"
            label="Item Sources"
            :items="userSources"
            item-text="display_name"
            item-value="source_id"
            multiple
            deletable-chips
            small-chips
            dense
            outlined
            hide-details
            return-object
            clearable>
          </v-autocomplete>
          <v-autocomplete 
            v-else-if="item.key === 'departments'"
            v-model="params[item.key]"
            :items="departments"
            :label="item.name"
            dense
            hide-details
            outlined
            clearable
            multiple
            small-chips
            deletable-chips
            @change="checkDownstream(item)"
            @click:clear="removeFilter(item.key)">
          </v-autocomplete>
          <v-autocomplete 
            v-else-if="item.key === 'categories'"
            v-model="params[item.key]"
            :items="categories"
            :label="item.name"
            dense
            hide-details
            outlined
            clearable
            multiple
            small-chips
            deletable-chips
            @change="checkDownstream(item)"
            @click:clear="removeFilter(item.key)">
          </v-autocomplete>
          <v-autocomplete 
            v-else-if="item.key === 'subcategories'"
            v-model="params[item.key]"
            :items="subcategories"
            :label="item.name"
            dense
            hide-details
            outlined
            clearable
            multiple
            small-chips
            deletable-chips
            @change="checkDownstream(item)"
            @click:clear="removeFilter(item.key)">
          </v-autocomplete>
          <v-autocomplete
            v-else-if="item.key === 'status'"
            v-model="params[item.key]"
            :items="['VALID', 'DISCONTINUED ITEM', 'TEMP OUT', 'NEW ITEM']"
            :label="item.name"
            dense
            hide-details
            outlined
            clearable
            @change="handleInputValue(item)"
            @click:clear="removeFilter(item.key)">
          </v-autocomplete>
          <div v-else-if="item.key == 'upc'">
            <v-text-field
              v-model="params[`${item.key}`]"
              :label="item.name"
              hide-details
              background-color="input"
              :prefix="item.prefix || null"
              :type="item.type"
              :autofocus="(selectedFilters.length === 0) && item.key === 'description'"
              dense
              clearable
              @input="attrs.inputValue ? null : handleInputValue(item)"
              @click:clear="removeFilter(item.key)"
              @keydown.enter="updateSearchFilters(true)"
              outlined
            >
            </v-text-field>
            <div v-if="suggestedUpc && params.upc" class="upcConfirm" style="color: #333">
              <div>
                Searching for {{suggestedUpc}}
                <a @click="$emit('updateUpc'); " style="font-weight: 500; text-decoration: underline; margin-left: 5px; font-style: normal">
                  undo
                </a>
                <span style="font-style: normal; margin: 0px 10px">|</span>
                <a @click="$emit('searchSimilar')" style="font-weight: 500; text-decoration: underline;font-style: normal">
                search for similar UPC's
                </a>
              </div>
            </div>
          </div>
          <v-text-field
            v-else
            v-model="params[`${item.key}`]"
            :label="item.name"
            hide-details
            background-color="input"
            :prefix="item.prefix || null"
            :type="item.type"
            :autofocus="(selectedFilters.length === 0) && item.key === ($auth.tenant === 'alliance-retail-group' ? 'item_id' : 'description')"
            dense
            clearable
            @input="attrs.inputValue ? null : handleInputValue(item)"
            @click:clear="removeFilter(item.key)"
            @keydown.enter="updateSearchFilters(true)"
            outlined>
          </v-text-field>
        </v-list-item-content>
      </v-list-item>
    </template>
    <template v-slot:append-item>
      <div class="mx-10 my-4">
        <v-row align="center" justify="center">
          <v-col cols="5" v-for="item in booleanFilters" :key="item.key" class="boolean">
            <v-switch
              v-model="params[`${item.key}`]"
              class="pa-0 ma-0"
              :label="item.name"
              dense
              hide-details
              @click="selectedFilters.includes(item.key) ? removeFilter(item.key): handleInputValue(item)">
          </v-switch>
        </v-col>
      </v-row>
      </div>
    </template>
  </v-select>
</template>
<script>
// components
import VendorSelect from '@/components/VendorSelect'
// mixins
import { userAccess } from '@/mixins/user-access'
// third-party
import { debounce } from 'lodash'
export default {
  data () {
    return {
      selectedFilters: [],
      selectedVendors: [],
      selectedSources: [],
      params: {
        description: '',
        item_id: '',
        upc: '',
        departments: [],
        categories: [],
        subcategories: [],
        vendor_ids: [],
        item_sources: [],
        private_label: false,
        shipper_flag: false,
        status: ''
      }
    }
  },
  name: 'SearchFilters',
  components: { VendorSelect },
  mixins: [userAccess],
  props: {
    value: Object,
    disabled: Boolean,
    originalUpc: String,
    suggestedUpc: String
  },
  watch: {
    activeFilters: {
      handler: debounce(function (newValue) {
        if (newValue) {
          this.updateSearchFilters()
        }
      }, 400),
      deep: true
    },
    selectedVendors: {
      handler (newValue) {
        if (newValue.length > 0) {
          sessionStorage.setItem('vendor_filters', JSON.stringify(newValue))
        } else {
          sessionStorage.removeItem('vendor_filters')
        }
        this.handleChangedVendors(newValue, 'vendor_ids')
      },
      deep: true
    },
    selectedSources: {
      handler (newValue) {
        if (newValue) {
          if (newValue.length > 0) {
            sessionStorage.setItem('source_filters', JSON.stringify(newValue))
          } else {
            sessionStorage.removeItem('source_filters')
          }
          this.handleChangedSources(newValue, 'item_sources')
        }
      },
      deep: true
    },
    'params.upc': {
      handler(newValue, oldValue) {
        if (newValue != oldValue && this.suggestedUpc) {
          this.$emit("clearUpc")
        }
      }
    }
  },
  created () {
    if (sessionStorage.vendor_filters) {
      this.selectedVendors = JSON.parse(sessionStorage.vendor_filters)
    }
    if (sessionStorage.source_filters) {
      this.selectedSources = JSON.parse(sessionStorage.source_filters)
    }
    if (sessionStorage.item_search_filters) {
      const stored = JSON.parse(sessionStorage.item_search_filters)
      this.params = { ...this.params, ...stored }
      const activeKeys = Object.keys(stored).filter(key => Boolean(stored[key]))
      if (activeKeys.length > 0) this.selectedFilters.push(...activeKeys)
    }
  },
  computed: {
    booleanFilters() {
      return this.filters.filter(f => f.type === 'boolean')
    },
    filters () {
      return [
        { name: 'Sources', key: 'item_sources', show: true },
        { name: 'Description', key: 'description', type: 'text', show: true },
        { name: this.$auth.tenant === 'alliance-retail-group' ? 'Item Code' : 'Item Id', key: 'item_id', type: 'text', show: true },
        { name: 'UPC', key: 'upc', type: 'text', show: true },
        { name: 'GTIN', key: 'gtin', type: 'text', show: this.$auth.tenant === 'pricechopper' },
        { name: 'Department', text: 'Dept', key: 'departments', type: 'text', show: true },
        { name: 'Category', key: 'categories', type: 'text', show: true },
        { name: 'Subcategory', key: 'subcategories', type: 'text', show: true },
        { name: 'Vendors', key: 'vendor_ids', show: !(this.$auth.tenant === 'jbg' && this.isRetailer) },
        { name: 'Linked Group', key: 'linked_group', show: this.$auth.tenant === 'alliance-retail-group' },
        { name: 'Private Label', key: 'private_label', type: 'boolean', show: this.$auth.tenant === 'jbg' },
        { name: 'Shipper', key: 'shipper_flag', type: 'boolean', show: this.$auth.tenant === 'jbg' },
        { name: 'Status', key: 'status', type: 'select', show: this.$auth.tenant === 'jbg' && this.isAdmin }
      ].filter(item => item.show)
    },
    activeFilters () {
      return Object.keys(this.params).filter(this.isActive)
    },
    departmentCategoryMapping() {
      return this.$store.getters.departmentCategoryMapping
    },
    categorySubcategoryMapping() {
      return this.$store.getters.categorySubcategoryMapping
    },
    departments () {
      let departments = [...new Set(this.departmentCategoryMapping.map(item => item.column))]
      if (this.isRetailer && this.$auth.tenant === 'jbg') {
        departments = departments.filter(item => !['LIQUOR WAREHOUSE'].includes(item))
      }
      return departments.sort((a, b) => a.localeCompare(b))
    },
    categories () {
      let categories = []
      if (this.params[`departments`].length > 0) {
        categories = [...new Set(this.departmentCategoryMapping.filter(item => this.params[`departments`].includes(item.column)).map(item => item.downstream_column))]
      } else {
        categories = [...new Set(this.departmentCategoryMapping.map(item => item.downstream_column))]
      }
      if (this.isRetailer && this.$auth.tenant === 'jbg') {
        categories = categories.filter(item => !['BEER', 'WINE', 'SPIRITS'].includes(item))
      }
      return categories.sort((a, b) => a.localeCompare(b))
    },
    subcategories () {
      var subcategories = []
      if (this.params[`categories`].length > 0) {
        subcategories = [...new Set(this.categorySubcategoryMapping.filter(item => this.params[`categories`].includes(item.column)).map(item => item.downstream_column))]
      } else {
        if (this.params[`departments`].length > 0) {
          subcategories = [...new Set(this.categorySubcategoryMapping.filter(item => this.categories.includes(item.column)).map(item => item.downstream_column))]
        } else {
          subcategories = [...new Set(this.categorySubcategoryMapping.map(item => item.downstream_column))]
        }
      }
      return subcategories.sort((a, b) => a.localeCompare(b))
    }
  },
  methods: {
    isActive (key) {
      if (this.isSelected(key) && Boolean(this.params[key])) {
        const value = this.params[key]
        return Array.isArray(value) ? (value.length > 0) : true
      }
      return false
    },
    getFilterText (item) {
      return `${item.text || item.name}: "${this.params[item.key]}"`
    },
    removeFilter (key, clearValue = false) {
      const index = this.selectedFilters.indexOf(key)
      if (index > -1) {
        this.selectedFilters.splice(index, 1)
        if (clearValue) {
          if (['departments', 'categories', 'subcategories'].includes(key)) this.params[key] = []
          else this.params[key] = null
        }
      }
    },
    removeVendor (id) {
      const index = this.selectedVendors.findIndex(v => v.id === id)
      if (index > -1) {
        this.selectedVendors.splice(index, 1)
      }
    },
    removeSource (source) {
      const index = this.selectedSources.findIndex(v => v === source)
      if (index > -1) {
        this.selectedSources.splice(index, 1)
      }
    },
    handleInputValue: debounce(function (item) {
      if ((this.params[item.key].length > 0 || (['private_label', 'shipper_flag'].includes(item.key) && this.params[item.key] === true)) && !this.isSelected(item.key)) {
          this.selectedFilters.push(item.key)
      }
    }, 200),
    handleChangedVendors (newValue, key) {
      this.params.vendor_ids = newValue.flatMap(v => v ? v.attributes.VENDOR_ID : [])
      if ((newValue.length > 0) && !this.isSelected(key)) {
        this.selectedFilters.push(key)
      } else if ((newValue.length === 0) && this.isSelected(key)) {
        this.removeFilter(key)
      }
    },
    handleChangedSources (newValue, key) {
      this.params.item_sources = newValue.flatMap(v => v ? v.source_id : [])
      if ((newValue.length > 0) && !this.isSelected(key)) {
        this.selectedFilters.push(key)
      } else if ((newValue.length === 0) && this.isSelected(key)) {
        this.removeFilter(key)
      }
    },
    isSelected (key) {
      return this.selectedFilters.includes(key)
    },
    updateSearchFilters (callSearch = false) {
      const filtersObj = this.activeFilters.reduce((obj, key) => {
        obj[key] = this.params[key]
        return obj
      }, {})
      this.$emit('filterInput', filtersObj, callSearch)
      if (this.activeFilters.length > 0) {
        sessionStorage.setItem('item_search_filters', JSON.stringify(filtersObj))
      } else {
        sessionStorage.removeItem('item_search_filters')
      }
    },
    checkDownstream(item) {
      let key = item.key
      if (this.params[key]) {
        this.handleInputValue(item)
        switch (key) {
          case 'departments':
            this.params.categories = this.params.categories.filter(item => this.categories.includes(item))
            if (this.params.categories.length == 0) {
              this.removeFilter('categories')
            } 
            // fall through
          case 'categories':
            this.params.subcategories = this.params.subcategories.filter(item => this.subcategories.includes(item))
            if (this.params.subcategories.length == 0) {
              this.removeFilter('subcategories')
            } 
            // fall through
        }
      }
    }
  }
}
</script>
<style>
.upcConfirm {
  font-size: .9em;
  font-style: italic;
  padding-top: 5px;
  padding-left: 5px;
}
.boolean {
  display: flex;
  justify-content: center;
}
</style>


// WEBPACK FOOTER //
// src/components/items/SearchFilters.vue